import type { LocalInterview } from '@Domain/Ports/LocalInterviews/LocalInterviewPort';
import type { OutboxItem, OutboxItemState } from '@Domain/Ports/Outbox/OutboxPort';
import PlainDate from '@Domain/PlainDate';
import InternalInterviewListEntryView from '@UI/Pages/Beneficiaries/Hooks/useBeneficiaryInterviewsList/InternalInterviewListEntryView';
import InternalInterviewParticipantView from '@UI/Pages/Beneficiaries/Hooks/useBeneficiaryInterviewsList/InternalInterviewParticipantView';
import localInterviewCaptureState from '@UI/Pages/Beneficiaries/Hooks/useBeneficiaryInterviewsList/localInterviewCaptureState';
import findLocalInterviewMatch from '@UI/Pages/Beneficiaries/Hooks/Helpers/findLocalInterviewMatch';
import resolveServerParticipants, {
	type InterviewParticipantLabels,
} from '@UI/Pages/Beneficiaries/Hooks/Helpers/resolveServerParticipants';
import type InterviewView from '@UI/Store/Interviews/Views/InterviewView';

const outboxStateFor = (
	outboxItemId: string | null,
	outboxItemsById: ReadonlyMap<string, OutboxItem>,
): OutboxItemState | null => {
	if (outboxItemId === null) {
		return null;
	}

	return outboxItemsById.get(outboxItemId)?.state ?? null;
};

/**
 * Derives the transfer badge of a server interview through `resumeState`, never through a second
 * reading of `recording.completedAt`: `'transfer-pending'` renders `'processing'`; `'none'` renders
 * `'sent'` only when a recording exists and is complete — `resumeState` also answers `'none'` when
 * `conductedOn` is unknown or consent was withdrawn, neither of which is "sent".
 */
const deriveServerTransferState = (serverInterview: InterviewView): 'processing' | 'sent' | null => {
	if (serverInterview.resumeState === 'transfer-pending') {
		return 'processing';
	}

	if (serverInterview.recording !== null && serverInterview.recording.completedAt !== null) {
		return 'sent';
	}

	return null;
};

const buildServerEntry = (
	serverInterview: InterviewView,
	matchingLocalInterview: LocalInterview | null,
	outboxItemsById: ReadonlyMap<string, OutboxItem>,
	labels: InterviewParticipantLabels,
): InternalInterviewListEntryView => {
	const outboxItemId = matchingLocalInterview?.outboxItemId ?? null;

	return new InternalInterviewListEntryView({
		key: String(serverInterview.id),
		origin: 'server',
		interviewId: serverInterview.id,
		clientReference: serverInterview.clientReference,
		localInterviewId: matchingLocalInterview?.id ?? null,
		outboxItemId,
		conductedOn: serverInterview.conductedOn,
		kind: serverInterview.kind.value,
		name: serverInterview.name,
		participants: resolveServerParticipants(serverInterview, labels),
		transferState: deriveServerTransferState(serverInterview),
		captureState: null,
		outboxState: outboxStateFor(outboxItemId, outboxItemsById),
		isConsentWithdrawn: serverInterview.isConsentWithdrawn,
		consentId: serverInterview.consentId,
		processingState: serverInterview.processingState,
	});
};

/**
 * A local interview the server has not confirmed yet — either mid-transfer, or a capture never
 * started (rule 5): `captureState` always reflects `localInterviewCaptureState`, never a second,
 * hand-rolled reading of `outboxItemId`. `participants` is rebuilt one-for-one from `LocalInterview`,
 * never resolved: the local port hands back plain objects read from device storage, never an instance
 * of this layer's own View, so each one is wrapped here rather than assigned verbatim. `label` was
 * captured at selection time (D-6), so this entry never reads `labels` and renders with no network and
 * no referential reachable.
 */
const buildLocalEntry = (
	localInterview: LocalInterview,
	outboxItemsById: ReadonlyMap<string, OutboxItem>,
): InternalInterviewListEntryView =>
	new InternalInterviewListEntryView({
		key: localInterview.id,
		origin: 'local',
		interviewId: localInterview.interviewId,
		clientReference: localInterview.id,
		localInterviewId: localInterview.id,
		outboxItemId: localInterview.outboxItemId,
		conductedOn: PlainDate.fromApi(localInterview.conductedOn),
		kind: localInterview.kind ?? null,
		name: localInterview.name ?? null,
		participants: localInterview.participants.map(
			(participant) => new InternalInterviewParticipantView(participant),
		),
		transferState: 'processing',
		captureState: localInterviewCaptureState(localInterview),
		outboxState: outboxStateFor(localInterview.outboxItemId, outboxItemsById),
		isConsentWithdrawn: false,
		consentId: null,
		processingState: null,
	});

/**
 * Orders the fused list: `conductedOn` descending, an unknown date last, never first. At an equal
 * date the local entry passes ahead — it is the more recent one, born on this device before the
 * server ever saw it. Between two local entries sharing a date, `createdAt` (the local port's own
 * clock) breaks the tie, descending: without it, list order would depend on store iteration order
 * and reorder itself between renders.
 */
const compareEntries = (
	first: InternalInterviewListEntryView,
	second: InternalInterviewListEntryView,
	localInterviewsByKey: ReadonlyMap<string, LocalInterview>,
): number => {
	const firstKey = first.conductedOn?.toApi() ?? null;
	const secondKey = second.conductedOn?.toApi() ?? null;

	if (firstKey !== secondKey) {
		if (firstKey === null) {
			return 1;
		}

		if (secondKey === null) {
			return -1;
		}

		return firstKey < secondKey ? 1 : -1;
	}

	if (first.origin !== second.origin) {
		return first.origin === 'local' ? -1 : 1;
	}

	if (first.origin === 'local' && second.origin === 'local') {
		const firstCreatedAt = localInterviewsByKey.get(first.key)?.createdAt ?? 0;
		const secondCreatedAt = localInterviewsByKey.get(second.key)?.createdAt ?? 0;

		return secondCreatedAt - firstCreatedAt;
	}

	return 0;
};

/**
 * Decides which entries the folder's interview list renders, from the two sources that can each
 * hold an interview and the queue that tracks its transfer. Pure: no port, no `Infra/` import, no
 * side effect — under `UI/Pages/` rather than `Application/` precisely because it reads
 * `InterviewView`, and `Application/` never imports `@UI`.
 *
 * Fuses before sorting, and sorts the full list: this function never truncates. The section that
 * renders only its first entries owns that decision, and must never truncate a `'processing'` entry
 * — it carries an action the user may still need to reach.
 *
 * `labels` is required, never optional: a caller that forgot it must be rejected by the compiler,
 * never silently fall back to rendering bare ids. Only `buildServerEntry` ever reads it — a local
 * entry's participants are already carried, labels included, by `LocalInterview` itself (D-6), which
 * is what keeps this function's local path free of any referential and this function itself pure.
 */
const mergeInterviewEntries = (
	serverInterviews: readonly InterviewView[],
	localInterviews: readonly LocalInterview[],
	outboxItems: readonly OutboxItem[],
	labels: InterviewParticipantLabels,
): InternalInterviewListEntryView[] => {
	const outboxItemsById = new Map(outboxItems.map((outboxItem) => [outboxItem.id, outboxItem]));
	const matchedLocalInterviewIds = new Set<string>();

	const serverEntries = serverInterviews.map((serverInterview) => {
		// Excludes already-matched locals from the candidates: `findLocalInterviewMatch` only ever sees
		// what remains available at this point in the iteration, one server interview at a time.
		const availableLocalInterviews = localInterviews.filter(
			(localInterview) => !matchedLocalInterviewIds.has(localInterview.id),
		);

		const matchingLocalInterview = findLocalInterviewMatch(
			serverInterview.id,
			serverInterview.clientReference,
			availableLocalInterviews,
		);

		if (matchingLocalInterview !== null) {
			matchedLocalInterviewIds.add(matchingLocalInterview.id);
		}

		return buildServerEntry(serverInterview, matchingLocalInterview, outboxItemsById, labels);
	});

	const unmatchedLocalInterviews = localInterviews.filter(
		(localInterview) => !matchedLocalInterviewIds.has(localInterview.id),
	);
	const localInterviewsByKey = new Map(
		unmatchedLocalInterviews.map((localInterview) => [localInterview.id, localInterview]),
	);
	const unmatchedLocalEntries = unmatchedLocalInterviews.map((localInterview) =>
		buildLocalEntry(localInterview, outboxItemsById),
	);

	return [...serverEntries, ...unmatchedLocalEntries].sort((first, second) =>
		compareEntries(first, second, localInterviewsByKey),
	);
};

export default mergeInterviewEntries;
